import { store } from "../store";
import { Tag } from "../types/tags.types";
import { api } from "../http";
import { DefaultRes } from "../types";
import { AxiosError, AxiosResponse } from "axios";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";

export class TagsService {
    public static readonly dispatch = store.dispatch.tags;
    public static readonly state = store.getState();

    /**
     * Fetches all tags.
     * @param callback Optional callback for handling responses.
     * @returns The fetched tags.
     */
    public static async fetchAll(
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Tag[] | void> {
        const {
            dispatch: { toggleLoading, setTags },
        } = TagsService;

        toggleLoading("fetchAll", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Tag[]>,
                AxiosResponse<DefaultRes<Tag[]>>
            >(`tags/list-tags`);

            toggleLoading("fetchAll", false);

            if (isValid) {
                setTags(result);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("fetchAll", false);
            // Callers treat a tag-fetch failure as non-critical and degrade to
            // ids/blank tags, so this is the only place it leaves a trace.
            console.error('[TagsService] fetchAll failed; tag names unavailable:', error?.message ?? String(error));

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Tag>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Creates a new tag.
     */
    public static async createTag(
        tag: any,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Tag | void> {
        const {
            dispatch: { toggleLoading, setTags },
        } = TagsService;

        toggleLoading("createTag", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Tag>,
                AxiosResponse<DefaultRes<Tag>>
            >(`tags/create-tag`, tag);

            toggleLoading("createTag", false);

            if (isValid) {
                const state = store.getState();
                const newItems = [...state.tags.items, result];
                setTags(newItems);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("createTag", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Tag>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
 * Deletes a tag and removes it from all groups and teams in the store.
 */
    public static async deleteTag(
        tagId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Tag | void> {
        const {
            dispatch: {
                toggleLoading,
                setTags,
            },
        } = TagsService;

        toggleLoading("deleteTag", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Tag>,
                AxiosResponse<DefaultRes<Tag>>
            >(`tags/delete-tag/${tagId}`);

            toggleLoading("deleteTag", false);

            if (isValid) {
                const state = store.getState();

                // 1. Remove the tag from the tag store
                const filteredTags = state.tags.items.filter(t => t._id !== tagId);
                setTags(filteredTags);

                // 2. Remove tag from teams
                const updatedTeams = state.teams.items.map(team => ({
                    ...team,
                    tags: team.tags.filter(t => t !== tagId),
                }));
                store.dispatch.teams.setTeams(updatedTeams);

                // 3. Remove tag from groups
                const updatedGroups = state.groups.items.map(group => ({
                    ...group,
                    tags: group.tags?.filter(t => t !== tagId) ?? null,
                }));
                store.dispatch.groups.setGroups(updatedGroups);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("deleteTag", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Tag>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }


}
